import { formatCompactNumber } from '@/lib/utils/formatNumber';
import type { PublicDeliverableAggregate, SharedMetricsData } from '../types/sharedAnalytics.types';
import type { TopInfluencer } from '../types/analytics.types';
import type { TopPost } from '@/hooks/useTopPerformingPosts';

export interface SharedRawAggregates {
  totalFollowers: number;
  totalEngagement: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalInfluencers: number;
  totalPosts: number;
  totalViews: number;
}

export function calculateSharedAggregates(
  aggregates: PublicDeliverableAggregate[]
): SharedRawAggregates {
  const totalFollowers = aggregates.reduce(
    (s, d) => s + (d.influencer_followers || 0),
    0
  );
  const totalLikes = aggregates.reduce((s, d) => s + (d.total_likes || 0), 0);
  const totalComments = aggregates.reduce((s, d) => s + (d.total_comments || 0), 0);
  const totalShares = aggregates.reduce((s, d) => s + (d.total_shares || 0), 0);
  const totalEngagement = aggregates.reduce(
    (s, d) =>
      s +
      (d.total_likes || 0) +
      (d.total_comments || 0) +
      (d.total_shares || 0) +
      (d.total_saves || 0),
    0
  );
  const totalPosts = aggregates.reduce((s, d) => s + (d.post_count || 0), 0);
  const totalViews = aggregates.reduce((s, d) => s + (d.total_views || 0), 0);
  const totalInfluencers = aggregates.length;

  return {
    totalFollowers,
    totalEngagement,
    totalLikes,
    totalComments,
    totalShares,
    totalInfluencers,
    totalPosts,
    totalViews,
  };
}

export function calculateSharedMetrics(
  raw: SharedRawAggregates,
  summaryData?: { cpv?: number | null; cpe?: number | null } | null
): SharedMetricsData {
  const cpvVal = summaryData?.cpv;
  const cpeVal = summaryData?.cpe;
  const totalReach = raw.totalFollowers * 0.9;
  const engagementRate =
    totalReach > 0
      ? `${((raw.totalEngagement / totalReach) * 100).toFixed(1)}%`
      : '0%';

  return {
    influencers: raw.totalInfluencers,
    posts: raw.totalPosts,
    views: formatCompactNumber(raw.totalViews),
    engagement: formatCompactNumber(raw.totalEngagement),
    engagementRate,
    followers: formatCompactNumber(raw.totalFollowers),
    cpv: cpvVal !== null && cpvVal !== undefined ? `₹${Number(cpvVal).toFixed(2)}` : '₹0.00',
    cpe: cpeVal !== null && cpeVal !== undefined ? `₹${Number(cpeVal).toFixed(2)}` : '₹0.00',
    totalLikes: formatCompactNumber(raw.totalLikes),
    totalComments: formatCompactNumber(raw.totalComments),
    totalShares: formatCompactNumber(raw.totalShares),
  };
}

export function calculateSharedTopInfluencers(
  aggregates: PublicDeliverableAggregate[],
  showAll: boolean = false
): TopInfluencer[] {
  if (aggregates.length === 0) return [];

  const sorted = [...aggregates].sort((a, b) => {
    if (a.cpe === null && b.cpe === null) return 0;
    if (a.cpe === null) return 1;
    if (b.cpe === null) return -1;
    return (a.cpe || 0) - (b.cpe || 0);
  });

  const sliced = showAll ? sorted : sorted.slice(0, 5);

  return sliced.map((d) => ({
    name: d.influencer_name,
    avatar: d.influencer_avatar ?? null,
    followers: formatCompactNumber(d.influencer_followers || 0),
    views: formatCompactNumber(d.total_views),
    engagement: formatCompactNumber(d.total_engagement),
    cpe: d.cpe !== null ? `₹${Number(d.cpe).toFixed(2)}` : '₹0.00',
    cpv: d.cpv !== null ? `₹${Number(d.cpv).toFixed(2)}` : '₹0.00',
    roi: d.er_views !== null ? `${Number(d.er_views).toFixed(1)}%` : '0%',
  }));
}
